import type { CSSProperties, MouseEvent } from 'react';
import MapLetter from './MapLetter';
import { GLANCE_KEY, glanceMark, scoreMeta, scorecardOf, type LeaseSearchDeal, type ScorecardRowDef } from './leaseSearch';

/** One of the broker's groups as the page reads it: its key, the label its heading prints, its colour, its buildings. */
export interface GlanceGroup {
  key: string;
  label: string;
  color?: string;
  rows: LeaseSearchDeal[];
}

/**
 * Every building on one line (a tenant's version of VP8.4's terms table): the columns ARE the rows of his spec, so
 * the table reads as "which buildings meet which line of what I asked for".
 *
 * ⛔ EVERY CELL IS A QUOTE - the scorecard's own `actual`, marked the same way as on the card. A building that is
 * not scored (an off-market target) leaves its cells EMPTY: never a dash a reader could take for a miss.
 *
 * A phone gets a different drawing of the same buildings (TP7, his ask 2026-10-13: "see on mobile all of the
 * properties at a glance instead of having to scroll down"). Drawn as a small card per building the table ran
 * 11,846px on Carlos's page at 390px - about 16 screens. So the phone's list is one line per building under its
 * group's heading, a mark under each line of his spec, and a tap goes to the building's full card. Both are in the
 * DOM; the stylesheet draws the list on a phone's SCREEN only, and the table on a wider screen and on paper.
 */
export default function LeaseSearchTable({
  groups,
  rows,
  onGoToDeal,
}: {
  groups: GlanceGroup[];
  rows: ScorecardRowDef[];
  onGoToDeal?: (id: string) => void;
}) {
  const go = (id: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (!onGoToDeal) return;
    e.preventDefault();
    onGoToDeal(id);
  };
  const isScored = (d: LeaseSearchDeal): boolean => Boolean(scorecardOf(d)?.floors);
  // The column names and each line's marks share one grid, so a mark always sits under its own row's name.
  const columns: CSSProperties = { gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))` };
  return (
    <>
      <div className="portal-lease-scroll">
        <table className="portal-lease-table">
          <thead>
            <tr>
              <th scope="col">Building</th>
              {rows.map((r) => (
                <th scope="col" key={r.key}>
                  {r.label}
                </th>
              ))}
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {groups.flatMap((g) =>
              g.rows.map((d) => {
                const floors = scorecardOf(d)?.floors;
                return (
                  <tr key={d.id}>
                    <th scope="row" style={{ borderLeftColor: g.color || undefined }}>
                      <a href={`#deal-${d.id}`} onClick={go(d.id)}>
                        {d.mapLetter && <MapLetter letter={d.mapLetter} color={g.color} />}
                        {d.name ?? d.id}
                      </a>
                      {d.city && <span className="portal-lease-city">{d.city}</span>}
                    </th>
                    {rows.map((r) => {
                      const f = floors?.[r.key];
                      const meta = f ? scoreMeta(f.status) : null;
                      return (
                        <td
                          key={r.key}
                          data-label={r.label}
                          className={`portal-score-cell${meta ? ` ${meta.className}` : ''}`}
                          title={f?.threshold ? `Your spec: ${f.threshold}` : undefined}
                        >
                          {f && (
                            /* ONE child: the mark, the figure and the screen reader's word travel as one piece. */
                            <span className="portal-lease-value">
                              {meta?.mark && <span aria-hidden="true">{meta.mark} </span>}
                              {f.actual}
                              {meta && <span className="portal-sr-only"> {meta.word}</span>}
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td data-label="Status">{d.status?.label && <span className="portal-lease-value">{d.status.label}</span>}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="portal-glance">
        {groups.some((g) => g.rows.some(isScored)) && (
          <p className="portal-glance-key">{GLANCE_KEY}. Tap a building for its full card.</p>
        )}
        {groups.map((g) => (
          <div className="portal-glance-group" key={g.key || 'other'} style={{ borderLeftColor: g.color || undefined }}>
            <h3 className="portal-glance-head">
              {g.label} <span className="portal-group-count">({g.rows.length})</span>
            </h3>
            {/* Named once per group, and only over a group that has marks to name - an unscored group gets no header. */}
            {g.rows.some(isScored) && (
              <div className="portal-glance-cols" style={columns} aria-hidden="true">
                {rows.map((r) => (
                  <span key={r.key}>{r.label}</span>
                ))}
              </div>
            )}
            <ol className="portal-glance-list">
              {g.rows.map((d) => {
                const floors = scorecardOf(d)?.floors;
                return (
                  <li key={d.id} data-glance-deal={d.id}>
                    <a className="portal-glance-name" href={`#deal-${d.id}`} onClick={go(d.id)}>
                      {d.mapLetter && <MapLetter letter={d.mapLetter} color={g.color} />}
                      {d.name ?? d.id}
                    </a>
                    {d.city && <span className="portal-glance-city"> · {d.city}</span>}
                    {/* An unscored building (an off-market target) gets no marks at all, never marks it has not earned. */}
                    {floors && (
                      <ul className="portal-glance-marks" style={columns}>
                        {rows.map((r) => {
                          const f = floors[r.key];
                          const meta = f ? scoreMeta(f.status) : null;
                          return (
                            <li key={r.key} data-glance-row={r.key} className={meta?.className}>
                              {meta && <span aria-hidden="true">{glanceMark(f?.status)}</span>}
                              {meta && <span className="portal-sr-only">{`${r.label ?? f?.label ?? r.key}: ${meta.word}`}</span>}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        ))}
      </div>
    </>
  );
}
